import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  MenuItem,
  Button,
  Divider,
  Chip,
  Avatar,
  IconButton,
  InputAdornment,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";

import EmptyState from "../components/EmptyState";
import Tag from "../components/Tag";
import OrganizerRequestDrawer from "./OrganizerRequestDrawer";
import { listenOrganizerRequests } from "../services/organizerRequests";

const StatusChip = ({ label, value, active, onClick, count }) => (
  <Chip
    label={
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <Typography sx={{ fontWeight: 900, fontSize: 12 }}>{label}</Typography>
        <Box
          sx={{
            px: 1,
            py: 0.2,
            borderRadius: 1,
            bgcolor: "rgba(11,18,32,0.06)",
            fontSize: 12,
            fontWeight: 900,
          }}
        >
          {count}
        </Box>
      </Box>
    }
    onClick={() => onClick(value)}
    variant={active ? "filled" : "outlined"}
    sx={{
      borderRadius: 1,
      bgcolor: active ? "rgba(37,99,235,0.10)" : "transparent",
      borderColor: active ? "rgba(37,99,235,0.22)" : "rgba(11,18,32,0.12)",
      color: active ? "primary.main" : "text.primary",
      fontWeight: 900,
      height: 34,
      "&:hover": {
        bgcolor: "rgba(37,99,235,0.08)",
      },
    }}
  />
);

export default function OrganizerRequests() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("pending");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const unsub = listenOrganizerRequests({ status: "" }, setRows);
    return () => unsub();
  }, []);

  const counts = useMemo(() => {
    const pending = rows.filter((r) => r.status === "pending").length;
    const approved = rows.filter((r) => r.status === "approved").length;
    const rejected = rows.filter((r) => r.status === "rejected").length;
    return { pending, approved, rejected, total: rows.length };
  }, [rows]);

  const filtered = useMemo(() => {
    const queryStr = q.trim().toLowerCase();
    return rows
      .filter((r) => (status ? r.status === status : true))
      .filter((r) => {
        if (!queryStr) return true;
        const blob = [
          r.id,
          r.organizer?.name,
          r.organizer?.email,
          r.organizer?.phone,
          r.event?.name,
          r.event?.cityState,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return blob.includes(queryStr);
      });
  }, [rows, status, q]);

  const clearFilters = () => {
    setQ("");
    setStatus("pending");
  };

  const span = (xs = 1, md = 1, lg = 1) => ({
    gridColumn: { xs: `span ${xs}`, md: `span ${md}`, lg: `span ${lg}` },
  });

  return (
    <Stack spacing={2.5}>
      {/* HEADER */}
      <Box sx={span(1, 2, 5)}>
        <Typography fontSize={{ xs: 22, md: 26 }} fontWeight={900}>
          Solicitações de criação de eventos
        </Typography>
        <Typography color="text.secondary">
          Analise as solicitações de criação de eventos para o site ONURB
        </Typography>
      </Box>
      {/* FILTROS + STATUS */}
      <Paper sx={{ p: 2.2, borderRadius: 1 }}>
        <Stack spacing={1.8}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.2}
            alignItems={{ md: "center" }}
          >
            <TextField
              label="Buscar"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Nome, e-mail, evento, cidade…"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: q ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setQ("")}
                      sx={{ borderRadius: 1 }}
                    >
                      <ClearRoundedIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />

            <TextField
              label="Status"
              select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              sx={{ minWidth: { xs: "100%", md: 220 } }}
            >
              <MenuItem value="pending">Pendentes</MenuItem>
              <MenuItem value="approved">Aprovadas</MenuItem>
              <MenuItem value="rejected">Reprovadas</MenuItem>
              <MenuItem value="">Todas</MenuItem>
            </TextField>

            <Button
              onClick={clearFilters}
              variant="outlined"
              sx={{ borderRadius: 1, height: 54 }}
            >
              Limpar
            </Button>
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            sx={{ gap: 1 }}
          >
            <StatusChip
              label="Pendentes"
              value="pending"
              active={status === "pending"}
              onClick={setStatus}
              count={counts.pending}
            />
            <StatusChip
              label="Aprovadas"
              value="approved"
              active={status === "approved"}
              onClick={setStatus}
              count={counts.approved}
            />
            <StatusChip
              label="Reprovadas"
              value="rejected"
              active={status === "rejected"}
              onClick={setStatus}
              count={counts.rejected}
            />
            <StatusChip
              label="Todas"
              value=""
              active={status === ""}
              onClick={setStatus}
              count={counts.total}
            />
          </Stack>
        </Stack>
      </Paper>

      {/* LISTA */}
      {filtered.length === 0 ? (
        <EmptyState
          title="Sem solicitações"
          subtitle="Nenhuma solicitação encontrada com esses filtros."
          actionLabel="Ver pendentes"
          onAction={() => setStatus("pending")}
        />
      ) : (
        <Paper sx={{ borderRadius: 1, overflow: "hidden" }}>
          {/* HEADER DA LISTA */}
          <Box sx={{ p: 2.2 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={0.6}
              alignItems={{ md: "center" }}
              justifyContent="space-between"
            >
              <Box>
                <Typography sx={{ fontWeight: 1000, fontSize: 14 }}>
                  {filtered.length} solicitações
                </Typography>
                <Typography
                  sx={{ color: "text.secondary", fontSize: 12, mt: 0.2 }}
                >
                  Clique em uma linha para ver detalhes e aprovar/reprovar.
                </Typography>
              </Box>

              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                Atualizado em tempo real
              </Typography>
            </Stack>
          </Box>

          <Divider />

          {/* CABEÇALHO DE COLUNAS (DESKTOP) */}
          <Box
            sx={{
              display: { xs: "none", md: "grid" },
              px: 2.2,
              py: 1.2,
              bgcolor: "rgba(11,18,32,0.03)",
              gridTemplateColumns: "1.4fr 1.6fr 1fr 170px 44px",
              gap: 1.2,
              alignItems: "center",
            }}
          >
            <Typography fontSize={12} fontWeight={900} color="text.secondary">
              Organizador
            </Typography>
            <Typography fontSize={12} fontWeight={900} color="text.secondary">
              Evento
            </Typography>
            <Typography fontSize={12} fontWeight={900} color="text.secondary">
              Contato
            </Typography>
            <Typography
              fontSize={12}
              fontWeight={900}
              color="text.secondary"
              sx={{ textAlign: "right" }}
            >
              Status
            </Typography>
            <Box />
          </Box>

          <Divider sx={{ display: { xs: "none", md: "block" } }} />

          {/* LINHAS */}
          <Box sx={{ display: "grid" }}>
            {filtered.map((r) => {
              const name = r.organizer?.name || "—";
              const email = r.organizer?.email || "—";
              const eventName = r.event?.name || "—";
              const city = r.event?.cityState || "—";
              const date = r.event?.date || "—";
              const phone = r.organizer?.phone || "—";
              const type = r.organizer?.type || "—";
              const initial = (name?.[0] || "O").toUpperCase();

              const statusLabel =
                r.status === "pending"
                  ? "Pendente"
                  : r.status === "approved"
                    ? "Aprovada"
                    : "Reprovada";

              return (
                <Box
                  key={r.id}
                  onClick={() => setSelected(r)}
                  role="button"
                  tabIndex={0}
                  sx={{
                    cursor: "pointer",
                    "&:hover": { bgcolor: "rgba(37,99,235,0.05)" },
                  }}
                >
                  <Box
                    sx={{
                      px: 2.2,
                      py: 1.6,
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        md: "1.4fr 1.6fr 1fr 170px 44px",
                      },
                      gap: 1.2,
                      alignItems: "center",
                    }}
                  >
                    {/* Organizador */}
                    <Stack direction="row" spacing={1.2} alignItems="center">
                      <Avatar
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: 1,
                          bgcolor: "rgba(37,99,235,0.10)",
                          color: "primary.main",
                          border: "1px solid rgba(37,99,235,0.18)",
                          fontWeight: 900,
                        }}
                      >
                        {initial}
                      </Avatar>

                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 950, lineHeight: 1.15 }}>
                          {name}
                        </Typography>
                        <Typography
                          sx={{ color: "text.secondary", fontSize: 12 }}
                        >
                          {email}
                        </Typography>
                      </Box>
                    </Stack>

                    {/* Evento */}
                    <Box>
                      <Typography sx={{ fontWeight: 900, lineHeight: 1.15 }}>
                        {eventName}
                      </Typography>
                      <Typography
                        sx={{ color: "text.secondary", fontSize: 12 }}
                      >
                        {city} • {date}
                      </Typography>
                    </Box>

                    {/* Contato */}
                    <Box>
                      <Typography sx={{ fontWeight: 900, lineHeight: 1.15 }}>
                        {phone}
                      </Typography>
                      <Typography
                        sx={{ color: "text.secondary", fontSize: 12 }}
                      >
                        {type}
                      </Typography>
                    </Box>

                    {/* Status */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: { xs: "flex-start", md: "flex-end" },
                      }}
                    >
                      <Tag label={statusLabel} tone={r.status} />
                    </Box>

                    {/* Chevron */}
                    <Box
                      sx={{
                        display: { xs: "none", md: "flex" },
                        justifyContent: "flex-end",
                      }}
                    >
                      <ChevronRightRoundedIcon
                        sx={{ color: "rgba(11,18,32,0.35)" }}
                      />
                    </Box>
                  </Box>

                  <Divider />
                </Box>
              );
            })}
          </Box>
        </Paper>
      )}

      <OrganizerRequestDrawer
        open={!!selected}
        item={selected}
        onClose={() => setSelected(null)}
      />
    </Stack>
  );
}
